import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRight, BarChart3, Check, Eye, EyeOff, Loader2, Lock, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { ApiError, authApi } from '../lib/api-client';
import { SkeletonRows } from '../components/ui/skeleton';
import { DefendIQMark } from '../components/DefendIQMark';

/** The 3D metallic emblem from the Claude Design prototype (`assets/defendiq-emblem.png` in that
 * project) — served from `apps/web/public/`, so it's just `/defendiq-emblem.png` at runtime,
 * same as any other Vite public-dir asset. Desktop-only (`lg:`): the mobile compact header still
 * uses the existing vector `DefendIQMark`, since the mobile layout is out of scope for this
 * redesign. */
const EMBLEM_URL = '/defendiq-emblem.png';

/**
 * Query params that only ever belong to the SSO callback round-trip
 * (`/auth/sso/callback?code=...&state=...`, or Entra's `?error=...&error_description=...`
 * on a provider-side rejection). If a user lands back on `/login` with any of these still
 * in the URL — e.g. navigating back manually — strip them so they don't linger in the URL
 * or browser history; harmless in principle, but they have no meaning on this route.
 */
const SSO_CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description'];

/**
 * `SsoCallback.tsx` redirects here with `?error=<key>` when the Microsoft round-trip itself
 * succeeded but DefendIQ denied the login (SSO_LOGIN_DENIED) — never for a raw token/network
 * failure, which it handles as a generic message on its own page instead. Keyed identically to
 * that page's own `Outcome['reason']` values.
 */
const SSO_DENIAL_MESSAGES: Record<string, string> = {
  not_a_member: "Your Microsoft account was verified, but you don't have access to DefendIQ. Contact your workspace administrator to request access.",
  disabled: 'Your DefendIQ account has been suspended. Contact your workspace administrator.',
};

/**
 * Derives a workspace slug from an email's domain: `rynos@bui.co.za` → `bui`,
 * `acme.com` → `acme`, `company.internal` → `company`. Returns `null` for anything
 * without an `@` or without a valid-looking domain (must have at least one `.` and
 * only alphanumeric/hyphen labels) — callers should fall back to an empty tenantSlug
 * in that case rather than guessing.
 */
export function extractWorkspaceSlug(email: string): string | null {
  const match = /^[^\s@]+@([a-z0-9-]+(?:\.[a-z0-9-]+)+)$/i.exec(email.trim());
  if (!match) return null;
  const [first] = match[1].split('.');
  return first ? first.toLowerCase() : null;
}

function isTenantNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === 'TENANT_NOT_FOUND';
}

export function Login() {
  const { login, ssoLogin } = useAuth();

  // null while the SSO config lookup is in flight; true/false once resolved
  // (or false on a failed lookup — fail safe to the password form, never a blank page).
  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(null);
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Reads `?error=<key>` (set by SsoCallback.tsx after a SSO_LOGIN_DENIED result) before
  // stripping SSO callback params from the URL on mount — the message itself renders through the
  // same styled error banner as every other error on this page (see the `error` state below).
  // An unrecognized key (or none) leaves `error` untouched. This is the only thing here that
  // touches `error`/`setError`; everything else about this cleanup is unchanged.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCallbackParams = SSO_CALLBACK_PARAMS.some((key) => params.has(key));
    if (!hasCallbackParams) return;
    const denialKey = params.get('error');
    if (denialKey && SSO_DENIAL_MESSAGES[denialKey]) {
      setError(SSO_DENIAL_MESSAGES[denialKey]);
    }
    SSO_CALLBACK_PARAMS.forEach((key) => params.delete(key));
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, []);

  // Look up whether this deployment has SSO configured at all, so we know whether to
  // show the Microsoft button, the password form, or both. Fails safe to the password
  // form on any error (network blip, etc.) — the page must always stay usable.
  useEffect(() => {
    let cancelled = false;
    authApi
      .ssoConfig()
      .then((config) => {
        if (!cancelled) setSsoEnabled(config.ssoEnabled);
      })
      .catch(() => {
        if (!cancelled) setSsoEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmailBlur = () => {
    setTenantSlug(extractWorkspaceSlug(email) ?? '');
  };

  const handlePasswordLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login({ tenantSlug, email, password });
    } catch (err) {
      if (isTenantNotFoundError(err)) {
        setError('Workspace not found. Contact your administrator.');
      } else {
        setError(
          err instanceof ApiError
            ? "That email, password, or workspace name doesn't match. Please try again."
            : 'Something went wrong. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSso = async () => {
    if (!tenantSlug) {
      setError('Enter your email first, then continue with Microsoft.');
      return;
    }
    setError(null);
    setBusy(true);
    setCheckingEmail(true);
    try {
      // Pre-SSO existence check — spares a doomed Microsoft round trip for an email that could
      // never pass the SSO callback's own membership check anyway. Purely informational: if the
      // check itself fails for any reason (network blip, rate limited, SSO not configured on this
      // deployment), fail OPEN and proceed to Microsoft as normal — the callback's membership
      // check is still the real, authoritative gate either way.
      let exists = true;
      try {
        exists = (await authApi.checkEmail({ email, tenantSlug })).exists;
      } catch {
        exists = true;
      }
      setCheckingEmail(false);
      if (!exists) {
        setError("No DefendIQ account found for this email address. Contact your administrator to request access.");
        return;
      }
      await ssoLogin(tenantSlug, email);
    } catch (err) {
      setError(
        isTenantNotFoundError(err)
          ? 'Workspace not found. Contact your administrator.'
          : 'Single sign-on is not available for this workspace.',
      );
    } finally {
      setBusy(false);
      setCheckingEmail(false);
    }
  };

  const showPasswordSection = ssoEnabled === false || showPasswordFallback;

  return (
    // Pinned to the dark palette regardless of the app-wide theme preference (BUI Dual-Theme
    // brief's login mockup is a fixed-dark design, not a themed one) — `[data-theme='dark']`
    // is a plain attribute selector (see styles/tokens.css), so scoping it here re-resolves every
    // existing ink/surface/brand/border token class beneath this element to its dark value, with
    // no need to hand-replace those classes individually.
    <main data-theme="dark" className="relative flex min-h-screen items-center justify-center overflow-y-auto overflow-x-hidden bg-canvas px-4 py-6 text-ink sm:px-6 lg:px-0 lg:py-0">
      <BackgroundEffects />

      {/* Below `lg:` this stays the original centered, bounded-height card (untouched) — the
          two-panel grid below is `lg:`-only, matching the Claude Design prototype's full-viewport
          `display:grid; grid-template-columns:1.15fr 1fr; min-height:100vh` (no `max-width` cap,
          no card chrome — see the card-look comment below for why). */}
      <section className="relative mx-auto flex w-full min-h-[calc(100vh-3rem)] max-w-[1180px] items-stretch justify-center lg:min-h-screen lg:max-w-none">
        {/* Card look (rounded corners/border/shadow/dark fill) kept for mobile; all four are
            cancelled at `lg:` since the panels' own full-bleed backgrounds (BrandPanel's `bg-shell`,
            the form panel's `bg-canvas`) take over and there's no card to draw a border/shadow
            around anymore. `lg:overflow-visible` matters too: this div's box would otherwise clip
            anything that assumed it could paint past its own edge. */}
        <div className="relative w-full overflow-hidden rounded-[24px] border border-white/10 bg-[#0B0F14] shadow-2xl lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none">
          <div className="grid h-full lg:grid-cols-[1.15fr_1fr]">
            <BrandPanel />

            {/* Unchanged below `lg:` (mobile keeps the centered, `items-center` card layout);
                `lg:p-12` (48px) and `lg:justify-center` reproduce the prototype's right-panel
                `justify-content:center; align-items:center; padding:48px` on `bg-canvas` — the
                same colour as this whole page, so no separate fill is needed here. */}
            <div className="relative flex items-center px-6 py-6 sm:px-10 lg:justify-center lg:p-12">
              <div className="relative mx-auto w-full max-w-[520px] lg:flex lg:max-w-[340px] lg:flex-col lg:gap-[22px]">
                <header className="mb-6 lg:mb-0">
                  <div className="mb-4 flex items-center gap-3 lg:hidden">
                    <DefendIQMark size={36} className="text-ink" />
                    <span className="text-lg font-semibold tracking-tight text-ink">
                      Defend<span className="text-brand">IQ</span>
                    </span>
                  </div>

                  {/* Not part of the prototype's heading block (it goes straight from nothing to
                      the "Welcome back" H2) — kept mobile-only so the existing small-screen layout
                      is unaffected. */}
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand lg:hidden">Secure access</p>

                  <h1 className="text-page-title font-bold tracking-tight text-ink sm:text-4xl lg:text-[22px] lg:tracking-[-0.01em]">
                    Welcome back
                  </h1>

                  <p className="mt-2 max-w-xl text-sm leading-6 text-ink-muted sm:text-base lg:mt-1.5 lg:max-w-none lg:text-[13px] lg:leading-normal">
                    Sign in to your DefendIQ account
                  </p>
                </header>

                {ssoEnabled === null ? (
                  <div className="mt-4" data-testid="sso-config-loading">
                    <SkeletonRows rows={3} />
                  </div>
                ) : (
                  <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
                    {/* Kept in the DOM so it's still submitted with login/SSO requests exactly
                        as before — just no longer a visible input, since the workspace is now
                        auto-detected from the email domain (see extractWorkspaceSlug). */}
                    <div className="sr-only">
                      <label htmlFor="tenantSlug">Workspace name</label>
                      <input id="tenantSlug" name="tenantSlug" value={tenantSlug} readOnly tabIndex={-1} />
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="email">Email</FieldLabel>
                      <InputShell>
                        <Mail className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={1.7} />
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          onBlur={handleEmailBlur}
                          placeholder="name@company.com"
                          autoComplete="email"
                          required
                          className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
                        />
                      </InputShell>
                      {tenantSlug ? (
                        <p className="text-xs text-ink-faint">
                          Signing into workspace: <span className="font-medium text-ink-muted">{tenantSlug}</span>
                        </p>
                      ) : null}
                    </div>

                    {error ? (
                      <p role="alert" className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-2.5 text-sm text-danger">
                        {error}
                      </p>
                    ) : null}

                    {ssoEnabled ? (
                      <button
                        type="button"
                        onClick={handleSso}
                        disabled={busy}
                        className="flex h-11 w-full items-center justify-center gap-3 rounded-lg bg-brand px-5 text-sm font-semibold text-white shadow-sm transition duration-150 hover:-translate-y-px hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-surface-raised disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {checkingEmail ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <MicrosoftMark />}
                        {checkingEmail ? 'Checking…' : 'Continue with Microsoft'}
                      </button>
                    ) : null}

                    {ssoEnabled && !showPasswordFallback ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-xs font-medium uppercase tracking-wide text-ink-faint">or</span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPasswordFallback(true)}
                          className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface px-5 text-sm font-semibold text-ink shadow-sm transition duration-150 hover:-translate-y-px hover:border-brand-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-surface-raised lg:h-[42px]"
                        >
                          Sign in with password
                        </button>
                      </>
                    ) : null}

                    {showPasswordSection ? (
                      <>
                        <div className="space-y-2">
                          <FieldLabel htmlFor="password">Password</FieldLabel>
                          <InputShell>
                            <LockKeyhole className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={1.7} />
                            <input
                              id="password"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={(event) => setPassword(event.target.value)}
                              placeholder="Enter your password"
                              autoComplete="current-password"
                              required
                              className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((current) => !current)}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              className="rounded-lg p-1.5 text-ink-faint transition hover:bg-surface hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-focus"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.7} /> : <Eye className="h-4 w-4" strokeWidth={1.7} />}
                            </button>
                          </InputShell>
                        </div>

                        <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                          <label className="inline-flex cursor-pointer items-center gap-3 text-ink-muted">
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={rememberMe}
                              onClick={() => setRememberMe((current) => !current)}
                              className={`flex h-5 w-5 items-center justify-center rounded-md border transition focus:outline-none focus:ring-2 focus:ring-focus ${
                                rememberMe ? 'border-brand bg-brand text-white' : 'border-border bg-surface'
                              }`}
                            >
                              {rememberMe && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                            </button>
                            Remember me
                          </label>

                          <button type="button" className="text-left font-medium text-brand transition hover:text-brand-hover focus:outline-none focus:underline">
                            Forgot password?
                          </button>
                        </div>

                        <button
                          type="submit"
                          disabled={busy}
                          className="group flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-border bg-surface px-5 text-sm font-semibold text-ink shadow-sm transition duration-150 hover:-translate-y-px hover:border-brand-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-surface-raised disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Sign in
                          <ArrowRight className="h-4 w-4 text-brand transition-transform duration-150 group-hover:translate-x-1" />
                        </button>
                      </>
                    ) : null}
                  </form>
                )}

                <footer className="mt-6 flex items-center justify-center gap-2 text-xs text-ink-faint lg:mt-0">
                  <ShieldCheck className="h-4 w-4" strokeWidth={1.7} />
                  Secure. Compliant. Trusted.
                </footer>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

const FEATURE_CARDS = [
  { icon: ShieldCheck, title: 'Authorized', description: 'Only test what you own' },
  { icon: Lock, title: 'Controlled', description: 'Built-in guardrails and approvals' },
  { icon: BarChart3, title: 'Auditable', description: 'Full visibility and traceability' },
] as const;

/**
 * Desktop-only brand panel (`hidden lg:flex` — mobile is out of scope for this redesign),
 * reproducing the Claude Design prototype's left column exactly: a logo lockup, a headline +
 * emblem row, then a three-column feature strip, over a `bg-shell` fill with a faint grid overlay.
 */
function BrandPanel() {
  return (
    <aside
      className="relative hidden overflow-hidden border-r border-border-subtle bg-shell lg:flex lg:flex-col lg:justify-between lg:overflow-visible lg:p-12"
      style={{ backgroundImage: 'radial-gradient(ellipse at top left, rgba(255,255,255,0.035), transparent 60%)' }}
    >
      {/* Extremely faint 48px grid, masked to fade in/out vertically — `--grid-line` (added to
          tokens.css) is the exact line colour the prototype specifies for each theme. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)',
        }}
      />

      {/* Top — logo lockup. */}
      <div className="relative z-10 flex items-center gap-3.5">
        <img src={EMBLEM_URL} alt="DefendIQ" width={34} height={34} className="shrink-0 object-contain" />
        <div className="h-[22px] w-px bg-border" />
        <div>
          <p className="text-base font-bold tracking-[-0.01em] text-ink">
            Defend<span className="text-brand">IQ</span>
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-faint">Authorized testing platform</p>
        </div>
      </div>

      {/* Middle — headline + emblem, side by side. */}
      <div className="relative z-10 flex items-center gap-7 py-12">
        <div className="max-w-[400px]">
          <h1 className="text-[40px] font-bold leading-[1.12] tracking-[-0.02em] text-ink">
            Security testing
            <br />
            that <span className="text-brand">builds trust.</span>
          </h1>
          <p className="mt-4 max-w-[400px] text-[15px] leading-[1.6] text-ink-muted">
            Authorized security testing with strict controls, auditable workflows, and enterprise-grade protection.
          </p>
        </div>
        <img
          src={EMBLEM_URL}
          alt="DefendIQ emblem"
          width={190}
          height={190}
          className="shrink-0 object-contain"
          style={{ filter: 'drop-shadow(0 18px 36px rgba(0,0,0,.35))' }}
        />
      </div>

      {/* Bottom — three feature columns. */}
      <div className="relative z-10 grid grid-cols-3 gap-6 border-t border-border-subtle pt-6">
        {FEATURE_CARDS.map(({ icon: Icon, title, description }) => (
          <div key={title} className="flex flex-col gap-2">
            <Icon className="h-[18px] w-[18px] text-brand" strokeWidth={2} />
            <span className="text-[13px] font-semibold text-ink">{title}</span>
            <span className="text-xs leading-[1.5] text-ink-faint">{description}</span>
          </div>
        ))}
      </div>
    </aside>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
      {children}
    </label>
  );
}

function InputShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-11 items-center gap-3 rounded-lg border border-border bg-surface px-4 transition duration-150 focus-within:border-brand-border focus-within:ring-2 focus-within:ring-focus lg:h-[42px] lg:bg-surface-raised">
      {children}
    </div>
  );
}

function MicrosoftMark() {
  return (
    <span aria-hidden="true" className="grid h-5 w-5 grid-cols-2 grid-rows-2 gap-[2px]">
      <span className="bg-white" />
      <span className="bg-white" />
      <span className="bg-white" />
      <span className="bg-white" />
    </span>
  );
}

function BackgroundEffects() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="security-grid absolute inset-0 opacity-[0.08]" />
    </div>
  );
}
